import React, { useEffect, useState } from "react";
import { inventoryHref } from "../../api/InventoryAPI";
import {Category, ParameterDataType, SelectionType} from "../../types/Category";
import { Unit } from "../../types/Unit";
import {
    InventorySummary,
    InventoryParameter
} from "../../types/Inventory";
import ParameterFilterInput from "../filters/ParameterFilterInput";
import { useParameterFilters } from "../../hooks/useParameterFilters";
import { DateComparison, SearchParameter, SelectableMatchMode } from "../../types/SearchFilter";
import { DATE_FORMAT_HINT } from "../../utils/dateFormat";
import ParameterInputField from "../filters/ParameterFilterInput";
import { searchStoreInventories } from "../../api/StoreAPI";
import { logApiError, getUserMessage } from "../../api/apiError";

interface InventorySearchPanelProps {
    token: string;
    category: Category;
    units?: Unit[];
    onInventorySelect: (id: string) => void;
    deletedInventoryId?: string | null;
}

const InventorySearchPanel: React.FC<InventorySearchPanelProps> = ({
                                                                       token,
                                                                       category,
                                                                       units = [],
                                                                       onInventorySelect,
                                                                       deletedInventoryId
                                                                   }) => {
    const categoryParams = category?.parameters ?? [];

    const {
        filters,
        rangeMode,
        setFilter,
        setRangeValue,
        toggleRange,
        resetFilters
    } = useParameterFilters();

    const [results, setResults] = useState<InventorySummary[]>([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [validationError, setValidationError] = useState<string | null>(null);

    useEffect(() => {
        resetFilters();
        setResults([]);
        setError(null);
        setValidationError(null);
    }, [category?.id]);

    useEffect(() => {
        if (!deletedInventoryId) return;
        setResults((prev) => prev.filter((inv) => inv.id !== deletedInventoryId));
    }, [deletedInventoryId]);

    const parseNumber = (val: string | undefined): number | undefined => {
        if (!val || val.trim() === "") return undefined;
        const num = Number(val);
        return isNaN(num) ? undefined : num;
    };

    const handleSearch = async () => {
        setLoading(true);
        setError(null);
        setValidationError(null);

        let localValidationError: string | null = null;

        const searchParameters: SearchParameter[] = categoryParams
            .map<SearchParameter | null>((p) => {
                const name = p.name;
                const val = filters[name];

                if (
                    p.dataType === ParameterDataType.INTEGER ||
                    p.dataType === ParameterDataType.DOUBLE
                )
                {
                    const minRaw = filters[`${name}_min`];
                    const maxRaw = filters[`${name}_max`];
                    const exact = val;

                    const min = parseNumber(minRaw);
                    const max = parseNumber(maxRaw);

                    // DOUBLE search must carry the unit its value is expressed in.
                    // INTEGER has no convertible units, so it carries none.
                    const isDouble = p.dataType === ParameterDataType.DOUBLE;
                    const allowedUnits = p.unitIds ?? (p.unitId ? [p.unitId] : []);
                    const selectedUnit = filters[`${name}_unit`] || allowedUnits[0];
                    const strictUnit = filters[`${name}_strict`] === "true";
                    const doubleUnitFields = isDouble ? { unit: selectedUnit, strictUnit } : {};

                    if (isDouble && !selectedUnit) {
                        localValidationError = `Please choose a unit for "${name}".`;
                        return null;
                    }

                    if (rangeMode[name]) {
                        if (minRaw && isNaN(Number(minRaw))) {
                            localValidationError = `Minimum value for "${name}" is not a valid number.`;
                            return null;
                        }
                        if (maxRaw && isNaN(Number(maxRaw))) {
                            localValidationError = `Maximum value for "${name}" is not a valid number.`;
                            return null;
                        }

                        if (min !== undefined && max !== undefined && min > max) {
                            localValidationError = `For "${name}", minimum cannot be greater than maximum.`;
                            return null;
                        }

                        if (min === undefined && max === undefined) return null;

                        return {
                            id: p.id,
                            dataType:
                                p.dataType === ParameterDataType.INTEGER
                                    ? "RANGE_INTEGER"
                                    : "RANGE_DOUBLE",
                            value: { minimum: min, maximum: max },
                            ...doubleUnitFields
                        };
                    }

                    if (exact?.trim()) {
                        return {
                            id: p.id,
                            dataType: p.dataType,
                            value: exact,
                            ...doubleUnitFields
                        };
                    }

                    return null;
                }

                // SELECTABLE
                if (p.dataType === ParameterDataType.SELECTABLE) {
                    if (!val || (Array.isArray(val) && val.length === 0)) {
                        return null;
                    }

                    const arr: string[] =
                        Array.isArray(val)
                            ? (val as string[])
                            : typeof val === "string" && val.trim()
                                ? val.split(",").map(v => v.trim()).filter(Boolean)
                                : [];

                    if (arr.length === 0) return null;

                    const st = p.selectionType ?? SelectionType.SINGLE;
                    return {
                        id: p.id,
                        dataType: p.dataType,
                        selectionType: st,
                        value: arr,
                        ...(st === SelectionType.MULTI
                            ? { matchMode: ((filters[`${name}_match`] as SelectableMatchMode) ?? "ANY") }
                            : {}),
                    };
                }

                // DATE (on / before / after / between)
                if (p.dataType === ParameterDataType.DATE) {
                    if (!val || String(val).trim() === "") return null;
                    const dateMode = (filters[`${name}_datemode`] as DateComparison) || "ON";
                    const date2 = filters[`${name}_date2`];
                    return {
                        id: p.id,
                        dataType: ParameterDataType.DATE,
                        mode: dateMode,
                        value: String(val),
                        ...(dateMode === "BETWEEN" && date2 ? { date2: String(date2) } : {}),
                    };
                }

                // STRING / BOOLEAN
                if (val?.trim() !== "") {
                    return {
                        id: p.id,
                        dataType: p.dataType,
                        value: val
                    };
                }

                return null;
            })
            .filter((p): p is SearchParameter => p !== null);

        if (localValidationError) {
            setValidationError(localValidationError);
            setLoading(false);
            return;
        }

        try {
            const body = {
                categoryId: category.id,
                searchParameters
            };
            const data = await searchStoreInventories(token, body);
            setResults(data);
        } catch (err: any) {
            logApiError(err);
            setError(getUserMessage(err));
        } finally {
            setLoading(false);
        }
    };

    const getParam = (
        inv: InventorySummary,
        paramName: string
    ): InventoryParameter | undefined =>
        inv.parameters?.find((p) => p.name === paramName);

    const toBoolean = (value: unknown): boolean => {
        if (typeof value === "boolean") return value;
        if (typeof value === "string") {
            const v = value.toLowerCase();
            return v === "true" || v === "1" || v === "yes";
        }
        if (typeof value === "number") return value !== 0;
        return false;
    };

    return (
        <div className="inventory-search-panel">
            <h3>Search Listings</h3>

            <div style={{ marginBottom: "0.5rem" }}>
                {categoryParams.length === 0 ? (
                    <p style={{ color: "#666" }}>
                        This category has no parameters to filter by.
                    </p>
                ) : (
                    <div className="param-filters">
                    {categoryParams.map((p) => {
                        const name = p.name;
                        const isNumeric = p.dataType === ParameterDataType.INTEGER || p.dataType === ParameterDataType.DOUBLE;
                        const isDouble = p.dataType === ParameterDataType.DOUBLE;
                        const allowedUnits = p.unitIds ?? (p.unitId ? [p.unitId] : []);
                        const selectedUnit = filters[`${name}_unit`] || allowedUnits[0];
                        const labelUnitId = isDouble ? selectedUnit : p.unitId;
                        const unitLabel = isNumeric && labelUnitId
                            ? (units.find(u => u.id === labelUnitId)?.name ?? labelUnitId)
                            : null;

                        return (
                            <div className="pf-row" key={name}>
                                <label className="pf-label">
                                    {unitLabel ? `${name} (${unitLabel})` : name}
                                    {p.description && (
                                        <div style={{ fontWeight: 400, fontSize: "0.78rem", color: "var(--text-secondary)" }}>{p.description}</div>
                                    )}
                                </label>
                                <div className="pf-control">
                                {p.dataType === ParameterDataType.INTEGER ||
                                p.dataType === ParameterDataType.DOUBLE ? (
                                    <>
                                    <ParameterInputField
                                        dataType={p.dataType}
                                        name={name}
                                        value={filters[name] ?? ""}
                                        useRange={rangeMode[name]}
                                        minValue={filters[`${name}_min`] ?? ""}
                                        maxValue={filters[`${name}_max`] ?? ""}
                                        onChange={setFilter}
                                        onRangeChange={setRangeValue}
                                        onToggleRange={toggleRange}
                                    />
                                    {isDouble && allowedUnits.length > 0 && (
                                        <div style={{ marginTop: "0.25rem", display: "flex", alignItems: "center", gap: "0.5rem", flexWrap: "wrap" }}>
                                            {allowedUnits.length > 1 && (
                                                <label style={{ fontSize: "0.82rem" }}>
                                                    Unit:{" "}
                                                    <select
                                                        value={selectedUnit ?? ""}
                                                        onChange={(e) => setFilter(`${name}_unit`, e.target.value)}
                                                    >
                                                        {allowedUnits.map((uid) => (
                                                            <option key={uid} value={uid}>
                                                                {units.find(u => u.id === uid)?.name ?? uid}
                                                            </option>
                                                        ))}
                                                    </select>
                                                </label>
                                            )}
                                            <label style={{ fontSize: "0.82rem" }}>
                                                <input
                                                    type="checkbox"
                                                    checked={filters[`${name}_strict`] === "true"}
                                                    onChange={(e) => setFilter(`${name}_strict`, e.target.checked ? "true" : "false")}
                                                />
                                                {" "}Strict unit
                                            </label>
                                        </div>
                                    )}
                                    </>
                                ) : p.dataType === ParameterDataType.SELECTABLE ? (
                                    <>
                                    <ParameterInputField
                                        dataType={ParameterDataType.SELECTABLE}
                                        name={name}
                                        value={filters[name] ?? ""}
                                        selectionType={p.selectionType ?? SelectionType.SINGLE}
                                        options={p.options ?? []}
                                        onChange={setFilter}
                                    />
                                    {(p.selectionType ?? SelectionType.SINGLE) === SelectionType.MULTI && (
                                        <div style={{ display: "flex", flexDirection: "column", alignItems: "flex-start", gap: "0.35rem", marginTop: "0.3rem", fontSize: "0.85rem" }}>
                                            Match:
                                            {(["ANY", "ALL"] as const).map((mode) => (
                                                <label key={mode} style={{ display: "inline-flex", alignItems: "center", gap: "0.3rem", fontWeight: 400, cursor: "pointer", margin: 0 }}>
                                                    <input
                                                        type="radio"
                                                        name={`${name}_match`}
                                                        checked={((filters[`${name}_match`] as string) ?? "ANY") === mode}
                                                        onChange={() => setFilter(`${name}_match`, mode)}
                                                    />
                                                    {mode === "ANY" ? "Any of the selected" : "All of the selected"}
                                                </label>
                                            ))}
                                        </div>
                                    )}
                                    </>
                                )  : p.dataType === ParameterDataType.BOOLEAN ? (
                                    <ParameterInputField
                                        dataType={ParameterDataType.BOOLEAN}
                                        name={name}
                                        value={(filters[name] as "" | "true" | "false") ?? ""}
                                        onChange={setFilter}
                                    />
                                ) : p.dataType === ParameterDataType.DATE ? (
                                    <div style={{ display: "flex", alignItems: "flex-start", gap: "0.5rem", flexWrap: "wrap" }}>
                                        <select
                                            value={(filters[`${name}_datemode`] as string) ?? "ON"}
                                            onChange={(e) => setFilter(`${name}_datemode`, e.target.value)}
                                            style={{ width: "8.5rem", flex: "0 0 auto" }}
                                        >
                                            <option value="ON">On</option>
                                            <option value="BEFORE">On or before</option>
                                            <option value="AFTER">On or after</option>
                                            <option value="BETWEEN">Between</option>
                                        </select>
                                        {/* Dates stack vertically so BETWEEN's two pickers align. */}
                                        <div style={{ display: "flex", flexDirection: "column", gap: "0.35rem" }}>
                                            <input
                                                type="date"
                                                value={(filters[name] as string) ?? ""}
                                                onChange={(e) => setFilter(name, e.target.value)}
                                                style={{ width: "13rem", padding: "0.4rem", fontSize: "1rem", fontFamily: "inherit" }}
                                            />
                                            {((filters[`${name}_datemode`] as string) ?? "ON") === "BETWEEN" && (
                                                <>
                                                    <span style={{ fontSize: "0.8rem", color: "var(--text-secondary)" }}>and</span>
                                                    <input
                                                        type="date"
                                                        value={(filters[`${name}_date2`] as string) ?? ""}
                                                        onChange={(e) => setFilter(`${name}_date2`, e.target.value)}
                                                        style={{ width: "13rem", padding: "0.4rem", fontSize: "1rem", fontFamily: "inherit" }}
                                                    />
                                                </>
                                            )}
                                        </div>
                                        <small style={{ width: "100%", color: "var(--text-secondary)", fontSize: "0.78rem" }}>
                                            <div>Date format: {DATE_FORMAT_HINT}</div>
                                            <div>Selected date{((filters[`${name}_datemode`] as string) ?? "ON") === "BETWEEN" ? "s are" : " is"} included in the results.</div>
                                        </small>
                                    </div>
                                ) : (
                                    <ParameterInputField
                                        dataType={ParameterDataType.STRING}
                                        name={name}
                                        value={filters[name] ?? ""}
                                        onChange={setFilter}
                                    />
                                )}
                                </div>
                            </div>
                        );
                    })}
                    </div>
                )}
            </div>

            <button onClick={handleSearch} disabled={loading}>
                {loading ? "Searching..." : "Search"}
            </button>

            {validationError && (
                <div className="error-message" style={{ marginTop: "0.5rem" }}>
                    {validationError}
                </div>
            )}

            {error && (
                <div className="error-message" style={{ marginTop: "0.5rem" }}>{error}</div>
            )}

            {results.length > 0 && (
                <div style={{ marginTop: "1rem" }}>
                    <h4>Results</h4>
                    <div className="results-table-wrapper">
                    <table>
                        <thead>
                        <tr>
                            <th
                                style={{
                                    borderBottom: "1px solid #ccc",
                                    textAlign: "left",
                                    width: "80px"
                                }}
                            >
                                Action
                            </th>
                            <th style={{ borderBottom: "1px solid #ccc" }}>Name</th>
                            <th style={{ borderBottom: "1px solid #ccc" }}>URL</th>
                            <th style={{ borderBottom: "1px solid #ccc" }}>Visibility</th>
                            {categoryParams.map((p) => (
                                <th
                                    key={p.name}
                                    style={{ borderBottom: "1px solid #ccc" }}
                                >
                                    {p.name}
                                </th>
                            ))}
                        </tr>
                        </thead>
                        <tbody>
                        {results.map((inv) => (
                            <tr key={inv.id}>
                                <td style={{ padding: "2px 4px" }}>
                                    <button onClick={() => onInventorySelect(inv.id)}>
                                        Edit
                                    </button>
                                </td>
                                <td style={{ padding: "2px 4px" }}>{inv.name}</td>
                                <td style={{ padding: "2px 4px" }}>
                                    {inv.url ? (
                                        <a
                                            href={inventoryHref(inv.url)}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                        >
                                            {inv.url}
                                        </a>
                                    ) : null}
                                </td>
                                <td style={{ padding: "2px 4px" }}>{inv.visibility}</td>
                                {categoryParams.map((p) => {
                                    const param = getParam(inv, p.name);
                                    if (p.dataType === ParameterDataType.BOOLEAN) {
                                        const checked = param ? toBoolean(param.value) : false;
                                        return (
                                            <td key={p.name} style={{ padding: "2px 4px" }}>
                                                <input type="checkbox" checked={checked} disabled />
                                            </td>
                                        );
                                    }
                                    const isNumeric = p.dataType === ParameterDataType.INTEGER || p.dataType === ParameterDataType.DOUBLE;
                                    // Prefer the item's own stated unit (each item may be entered in a
                                    // different allowed unit); fall back to the category's unit for integers.
                                    const cellUnitId = isNumeric
                                        ? (param?.units ?? p.unitId)
                                        : undefined;
                                    const unitLabel = cellUnitId
                                        ? (units.find(u => u.id === cellUnitId)?.name ?? cellUnitId)
                                        : null;
                                    const displayValue = param ? String(param.value) : "";
                                    return (
                                        <td key={p.name} style={{ padding: "2px 4px" }}>
                                            {displayValue}
                                            {unitLabel && displayValue && (
                                                <span style={{ marginLeft: "0.3rem", fontSize: "0.82rem", color: "var(--text-secondary)" }}>{unitLabel}</span>
                                            )}
                                        </td>
                                    );
                                })}
                            </tr>
                        ))}
                        </tbody>
                    </table>
                    </div>
                </div>
            )}

            {results.length === 0 && !loading && (
                <p style={{ marginTop: "0.5rem" }}>
                    No listings found yet. Enter some filters and click Search.
                </p>
            )}
        </div>
    );
};

export default InventorySearchPanel;